import React from 'react';
import {
  GraduationCap,
  Calendar,
  FileText,
  Clock,
  BookOpen,
  Award,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';

const Admissions = () => {

  const kindergartenDates = [
    { date: "Wednesday, December 9, 2026", event: "Kindergarten Application Opens" },
    { date: "Friday, January 22, 2027", event: "Kindergarten Application Closes" },
    { date: "Tuesday, March 30, 2027", event: "Kindergarten Offer Release" }
  ];

  const upperGradesDates = [
    { date: "Monday, April 19, 2027", event: "G&T Upper Grade Application Opens" },
    { date: "Friday, May 7, 2027", event: "G&T Upper Grade Application Closes" },
    { date: "Thursday, June 10, 2027", event: "G&T Upper Grade Offer Release" }
  ];

  return (
    <main id="main-content" className="bg-white pt-20">
      {/* Hero Section */}
      <section className="section-padding hero-gradient text-white">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 mb-8">
              <GraduationCap className="h-5 w-5" aria-hidden="true" />
              <span className="text-sm font-medium tracking-wide uppercase">Join Our Community</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              <span className="block font-extralight text-white/90">Welcome to</span>
              <span className="block font-medium">TAG Scholars</span>
            </h1>

            <p className="text-xl text-white/90 mb-12 font-light max-w-3xl mx-auto leading-relaxed">
              Join our community of talented and gifted young scholars where every child's potential is nurtured
              and excellence is cultivated through innovative education and caring support.
            </p>

            <div className="flex justify-center">
              <a
                href="https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/gifted-talented"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-900 hover:bg-white/95 font-medium px-10 py-4 rounded-lg transition-all duration-300 shadow-lg"
              >
                Start Application
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* Admission Process */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
              <Clock className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm font-medium">Admission Process</span>
            </div>
            <h2 className="text-4xl font-light text-primary-900 mb-6">Your Path to TAG</h2>
            <p className="text-lg text-primary-700 max-w-3xl mx-auto font-light">
              Our straightforward admission process is designed to identify students who will thrive in our
              challenging and supportive academic environment.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-12">
            {/* Kindergarten Admission Process */}
            <div className="bg-primary-50 rounded-2xl p-8 border border-primary-200">
              <div className="flex items-center space-x-3 mb-6">
                <GraduationCap className="h-8 w-8 text-primary-700" aria-hidden="true" />
                <h3 className="text-2xl font-medium text-primary-900">
                  G&T Admissions for Children Entering Kindergarten in Fall 2026
                </h3>
              </div>

              {/* Eligibility Process */}
              <div className="mb-8">
                <h4 className="text-xl font-medium text-primary-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-gold-700" aria-hidden="true" />
                  Eligibility Process
                </h4>

                <div className="space-y-6">
                  {/* DOE Pre-K Students */}
                  <div className="bg-white rounded-xl p-6 border border-primary-100">
                    <h5 className="font-medium text-primary-900 mb-3">
                      For current pre-K students at DOE/district schools, Pre-K Centers, CBOs, or charter schools:
                    </h5>
                    <p className="text-primary-700 font-light">
                      Anyone who adds a G&T program to their kindergarten application will be evaluated by their current pre-K teachers.
                    </p>
                  </div>

                  {/* Private/Parochial or Not in School */}
                  <div className="bg-white rounded-xl p-6 border border-primary-100">
                    <h5 className="font-medium text-primary-900 mb-3">
                      For current pre-K students at private or parochial school programs, or for children who are not yet in school:
                    </h5>
                    <ul className="space-y-2 text-primary-700 font-light">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Interested families apply by listing a G&T program on their kindergarten application.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>After the application closes, DOE's Division of Early Childhood Education contacts applicants to set up interviews.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Then, interviews take place.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Based on these interviews, early childhood education experts nominate eligible applicants for G&T admissions.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How Offers Are Made */}
              <div>
                <h4 className="text-xl font-medium text-primary-900 mb-4 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-gold-700" aria-hidden="true" />
                  How Kindergarten G&T Offers Are Made
                </h4>
                <div className="bg-white rounded-xl p-6 border border-primary-100">
                  <ul className="space-y-3 text-primary-700 font-light">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>After all applications are submitted and eligibility is determined, all eligible applicants are considered for offers.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Offers are determined based on families' application choices, seat availability, and admissions priorities. All G&T programs give an admissions priority to siblings of current students.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Due to limited seats, not all eligible applicants will be made offers.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Eligible applicants may be placed on program's waitlists if seats are not available.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>All kindergarten applicants who apply by the deadline will receive a results letter in spring 2026.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Grades 1-4 Admission Process */}
            <div className="bg-gold-50 rounded-2xl p-8 border border-gold-200">
              <div className="flex items-center space-x-3 mb-6">
                <BookOpen className="h-8 w-8 text-gold-700" aria-hidden="true" />
                <h3 className="text-2xl font-medium text-primary-900">
                  For Children Entering G&T Programs for 1st, 2nd, 3rd, or 4th Grade in Fall 2026
                </h3>
              </div>

              {/* Eligibility Process */}
              <div className="mb-8">
                <h4 className="text-xl font-medium text-primary-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary-600" aria-hidden="true" />
                  Eligibility Process
                </h4>

                <div className="space-y-6">
                  {/* NYC Public Schools */}
                  <div className="bg-white rounded-xl p-6 border border-gold-100">
                    <h5 className="font-medium text-primary-900 mb-3">
                      For current Kindergarten, 1st, 2nd, and 3rd graders in NYC public schools
                    </h5>
                    <p className="text-primary-700 font-light mb-4">
                      Students are considered eligible if they satisfy the following grade requirements on their most recently available report card.
                      Once a child is deemed eligible, there is no ranking within eligible students based on differences in course grades.
                    </p>
                    <ul className="space-y-2 text-primary-700 font-light">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Have course grades of a 3 or 4, or the equivalent, on their report card in Math, Reading and Writing.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Cannot have had a 1 in any marking period in Math, Reading, Writing, Social Studies and Science in the current school year.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Eligible students will receive notification either by email or mail.</span>
                      </li>
                    </ul>
                  </div>

                  {/* Charter/Private/Parochial Schools */}
                  <div className="bg-white rounded-xl p-6 border border-gold-100">
                    <h5 className="font-medium text-primary-900 mb-3">
                      For current Kindergarten, 1st, 2nd, and 3rd graders in charter, private or parochial schools
                    </h5>
                    <p className="text-primary-700 font-light mb-3">
                      In order to determine your child's eligibility, you must:
                    </p>
                    <ul className="space-y-2 text-primary-700 font-light">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Submit your child's report card grades to a Family Welcome Center.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>After review of your child's report grades, you will be notified of the eligibility determination within 2 business days.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>If your child is eligible, you will be provided with instructions about how to apply.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>NYC Public Schools will determine eligibility based on converting your child's grades to a 4-point scale.</span>
                      </li>
                    </ul>
                  </div>

                  {/* Homeschool */}
                  <div className="bg-white rounded-xl p-6 border border-gold-100">
                    <h5 className="font-medium text-primary-900 mb-3">
                      For current Kindergarten, 1st, 2nd, and 3rd graders in Homeschool
                    </h5>
                    <ul className="space-y-2 text-primary-700 font-light">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Email ESenrollment@schools.nyc.gov with your child's name, date of birth, current grade, and phone number in the spring.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Our team will contact you directly to discuss your child's academics in Math, Reading, Writing, Social Studies and Science to determine eligibility.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>You will be notified of the eligibility determination. If your child is eligible, you will be provided with instructions about how to apply.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How Offers Are Made */}
              <div>
                <h4 className="text-xl font-medium text-primary-900 mb-4 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-primary-600" aria-hidden="true" />
                  How Grade 1-4 G&T Offers Are Made
                </h4>
                <div className="bg-white rounded-xl p-6 border border-gold-100">
                  <ul className="space-y-3 text-primary-700 font-light">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Eligibility to apply to G&T programs does not guarantee a G&T seat. Offers for eligible applicants will be determined based on families' application choices, programs' seat availability, and admissions priorities.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>All G&T programs give priority to siblings of current students enrolled or pre-registered for Fall 2026. G&T programs do not give priority to zoned families.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>All applicants will receive a results letter, but due to limited seats, not all eligible applicants will receive an offer to a G&T program.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Families who receive offers will have the opportunity to accept or decline any offer that they receive.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Families with eligible students will be able to add themselves to additional waitlists of G&T programs if desired.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Dates */}
      <section className="section-padding bg-neutral-50">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto">
            <div>
              <div className="inline-flex items-center space-x-2 bg-gold-100 text-gold-700 px-4 py-2 rounded-full mb-6">
                <Calendar className="h-4 w-4" aria-hidden="true" />
                <span className="text-sm font-medium">Important Dates</span>
              </div>

              <h3 className="text-3xl font-light text-primary-900 mb-6">Timeline</h3>
              <p className="text-primary-700 mb-8 font-light">
                Mark these important dates on your calendar to stay on track with the admission process.
              </p>

              {/* Kindergarten Dates */}
              <div className="mb-10">
                <h4 className="text-lg font-medium text-primary-900 mb-4">
                  For Children Interested in Applying to Kindergarten G&T Programs in Fall 2027
                </h4>
                <p className="text-primary-700 mb-6 font-light">Here are some key dates:</p>
                <div className="space-y-4">
                  {kindergartenDates.map((item, index) => (
                    <div key={index} className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                      <div className="text-sm font-medium text-primary-700 mb-1">{item.date}</div>
                      <div className="text-primary-900 font-medium">{item.event}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upper Grades Dates */}
              <div>
                <h4 className="text-lg font-medium text-primary-900 mb-4">
                  For Children Interested in Applying to Grades 1, 2, 3, or 4 G&T Programs in Fall 2027
                </h4>
                <p className="text-primary-700 mb-6 font-light">Here are some key dates:</p>
                <div className="space-y-4">
                  {upperGradesDates.map((item, index) => (
                    <div key={index} className="bg-gold-50 rounded-lg p-4 border border-gold-100">
                      <div className="text-sm font-medium text-gold-700 mb-1">{item.date}</div>
                      <div className="text-primary-900 font-medium">{item.event}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="bg-primary-700 rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6">Learn More</h2>
            <p className="text-xl text-white mb-8 font-light max-w-2xl mx-auto">
              Learn about the NYC Department of Education's Gifted and Talented program and how to apply for admission.
            </p>

            <div className="flex justify-center mb-8">
              <a
                href="https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/gifted-talented"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-900 hover:bg-white/95 font-medium px-10 py-4 rounded-lg transition-all duration-300 shadow-lg"
                aria-label="Learn more about NYC DOE Gifted and Talented program"
              >
                Learn More
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t border-white/20">
              <div className="flex items-center justify-center space-x-3">
                <Phone className="h-5 w-5 text-gold-400" aria-hidden="true" />
                <div className="text-white/90 text-center">
                  <div>Phone: 212-860-6003 x1061</div>
                  <div>Fax: 212-831-1842</div>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Mail className="h-5 w-5 text-gold-400" aria-hidden="true" />
                <div className="text-white/90 text-center">
                  <a href="mailto:bbova@schools.nyc.gov" className="hover:text-white block">
                    bbova@schools.nyc.gov
                  </a>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <MapPin className="h-5 w-5 text-gold-400" aria-hidden="true" />
                <span className="text-white/90">240 East 109 Street, Manhattan, NY 10029</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Admissions;